import { Injectable, signal, computed } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading';

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    message = signal<string>('');
    show = signal<boolean>(false);
    type = signal<ToastType>('success');

    // Progress signals for loading-with-progress variant
    progressCurrent = signal<number>(0);
    progressTotal = signal<number>(0);
    progressStep = signal<string>('');
    progressTitle = signal<string>('');

    /** Whether the toast has structured progress data */
    hasProgress = computed(() => this.progressTotal() > 0 && this.type() === 'loading');

    /** Percentage 0-100 derived from current/total */
    progressPercentage = computed(() => {
        const total = this.progressTotal();
        if (total <= 0) return 0;
        return Math.round((this.progressCurrent() / total) * 100);
    });

    /** Whether progress just completed (100%) — triggers checkmark animation */
    progressCompleted = signal<boolean>(false);

    private timeoutId: any = null;

    private setToast(show: boolean, message: string, type: ToastType, persist = false): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        if (show !== this.show()) {
            this.show.set(show);
        }
        if (message !== this.message()) {
            this.message.set(message);
        }
        if (type !== this.type()) {
            this.type.set(type);
        }

        if (!persist) {
            this.timeoutId = setTimeout(() => {
                this.show.set(false);
                this.resetProgress();
                this.timeoutId = null;
            }, 4000);
        }
    }

    success(message: string, persist = false): void {
        this.setToast(true, message, 'success', persist);
    }

    error(message: string, persist = false): void {
        this.setToast(true, message, 'error', persist);
    }

    info(message: string, persist = false): void {
        this.setToast(true, message, 'info', persist);
    }

    warning(message: string, persist = false): void {
        this.setToast(true, message, 'warning', persist);
    }

    loading(message: string): void {
        this.setToast(true, message, 'loading', true);
    }

    /**
     * Shows a loading toast with structured progress data.
     * Enables the rich progress variant with circular SVG, progress bar, and step info.
     */
    loadingWithProgress(title: string, step: string, current: number, total: number): void {
        this.progressTitle.set(title);
        this.progressStep.set(step);
        this.progressCurrent.set(current);
        this.progressTotal.set(total);
        this.progressCompleted.set(false);
        this.setToast(true, title, 'loading', true);
    }

    /**
     * Shows a completion animation (checkmark) before closing the toast.
     * The checkmark stays visible for the duration, then the toast closes.
     */
    completeWithSuccess(message: string, duration = 3500): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.progressCurrent.set(this.progressTotal());
        this.progressCompleted.set(true);
        this.progressStep.set('');
        this.progressTitle.set(message);

        this.timeoutId = setTimeout(() => {
            this.show.set(false);
            this.resetProgress();
            this.timeoutId = null;
        }, duration);
    }

    hide(): void {
        this.show.set(false);
        this.resetProgress();
    }

    private resetProgress(): void {
        this.progressCurrent.set(0);
        this.progressTotal.set(0);
        this.progressStep.set('');
        this.progressTitle.set('');
        this.progressCompleted.set(false);
    }
}
